import { useEffect, useRef } from "react";

import { fetchMe, type MyProfileDTO } from "@/api/adapters/my-profile";

/**
 * Polls GET /me when the current user has a pending withdraw request, to detect
 * when an admin approves it (status flips from 'ACTIVE' → 'WITHDRAWN').
 *
 * Admin approval triggers immediate forced logout of the withdrawn medical staff.
 *
 * @param profile - The current profile from useMyProfile().
 * @param onApproved - Called when the withdrawal has been approved (status === 'WITHDRAWN').
 * @param intervalMs - Polling interval in milliseconds. Default 5_000 (5s).
 */
export function useWithdrawPolling(
	profile: MyProfileDTO | undefined,
	onApproved: () => void,
	intervalMs = 5_000,
): void {
	const onApprovedRef = useRef(onApproved);
	onApprovedRef.current = onApproved;

	// Immediate trigger: if the profile already shows WITHDRAWN (e.g. cached data
	// after a remount), fire the callback without waiting for a poll cycle.
	useEffect(() => {
		if (profile?.status === "WITHDRAWN") {
			onApprovedRef.current();
		}
	}, [profile?.status]);

	const hasPending = profile?.withdrawRequest !== null;
	const isActive = profile?.status === "ACTIVE";
	const shouldPoll = hasPending && isActive;

	useEffect(() => {
		if (!shouldPoll) return;

		let active = true;
		let timer: ReturnType<typeof setTimeout>;

		const poll = async () => {
			if (!active) return;
			try {
				const latest = await fetchMe();
				if (!active) return;
				if (latest.status === "WITHDRAWN") {
					onApprovedRef.current();
				}
			} catch {
				// Polling failure is non-fatal — the next interval will retry.
				// The profile query's error boundary handles persistent failures.
			}
			if (active) {
				timer = setTimeout(poll, intervalMs);
			}
		};

		// Fire the first poll immediately, then every intervalMs thereafter.
		poll();

		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [shouldPoll, intervalMs]);
}
